import React, { useEffect, useState } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { AuthProvider } from '@contexts/AuthContext';
import { ToastProvider } from '@contexts/ToastContext';
import { PortalAuthProvider, usePortalAuth } from '@contexts/PortalAuthContext';
import { useAuth } from '@contexts/AuthContext';
import { PageTitleProvider } from '@contexts/PageTitleContext';
import { AuthenticatedLayout } from './components/layout/AuthenticatedLayout';
import { RequireDemoSandbox } from './components/demo/RequireDemoSandbox';
import { ModuleUnderDevelopment } from './components/demo/ModuleUnderDevelopment';
import { DashboardLayout } from './components/layout/DashboardLayout';
import RequireOnboardingComplete from './guards/RequireOnboardingComplete';
import RequireMembership from './guards/RequireMembership';
import {
  RequireDemoAllowed,
  RequireGpsLive,
  RequireRoutesPlanner,
} from './guards/RequireFeatureFlag';
import {
  MEMBERSHIP_ADMIN_WRITE,
  MEMBERSHIP_APPROVAL_READ,
  MEMBERSHIP_AUDIT_READ,
  MEMBERSHIP_BILLING_READ,
  MEMBERSHIP_COMMERCIAL_READ,
  MEMBERSHIP_DOCKET_READ,
  MEMBERSHIP_FLEET_READ,
  MEMBERSHIP_OPS_READ,
  MEMBERSHIP_OPS_WRITE,
  MEMBERSHIP_TRIP_EXECUTE,
} from './utils/permissions';
import shipmentsService from './services/shipments.service';
import notificationsService from './services/notifications.service';
import './styles/compact.css';

const Landing = React.lazy(() => import('@pages/Landing'));
const Login = React.lazy(() => import('@pages/Login'));
const Register = React.lazy(() => import('@pages/Register'));
const ForgotPassword = React.lazy(() => import('@pages/ForgotPassword'));
const ResetPassword = React.lazy(() => import('@pages/ResetPassword'));
const VerifyEmail = React.lazy(() => import('@pages/VerifyEmail'));
const Dockets = React.lazy(() => import('@pages/Dockets'));
const DispatchBoard = React.lazy(() => import('@pages/DispatchBoard'));
const Tracking = React.lazy(() => import('@pages/Tracking'));
const MyTrips = React.lazy(() => import('@pages/MyTrips'));
const Carriers = React.lazy(() => import('@pages/Carriers'));
const CarrierDetail = React.lazy(() => import('@pages/CarrierDetail'));
const CompanySetup = React.lazy(() => import('@pages/CompanySetup'));
const Invoices = React.lazy(() => import('@pages/Invoices'));
const InvoiceDetail = React.lazy(() => import('@pages/InvoiceDetail'));
const DocketDetail = React.lazy(() => import('@pages/DocketDetail'));
const DocketLrPrintPage = React.lazy(() => import('@pages/DocketLrPrintPage'));
const Analytics = React.lazy(() => import('@pages/Analytics'));
const Reports = React.lazy(() => import('@pages/Reports'));
const CommercialAnalytics = React.lazy(() => import('@pages/CommercialAnalytics'));
const DemoWorkspace = React.lazy(() => import('@pages/DemoWorkspace'));
const Dashboard = React.lazy(() => import('@pages/Dashboard'));
const Fleet = React.lazy(() => import('@pages/Fleet'));
const VehicleDetail = React.lazy(() => import('@pages/VehicleDetail'));
const DriverDetail = React.lazy(() => import('@pages/DriverDetail'));
const RoutesPage = React.lazy(() => import('@pages/Routes'));
const RoutePlanning = React.lazy(() => import('@pages/RoutePlanning'));
const Yard = React.lazy(() => import('@pages/Yard'));
const GatePass = React.lazy(() => import('@pages/GatePass'));
const DockScheduling = React.lazy(() => import('@pages/DockScheduling'));
const Inbound = React.lazy(() => import('@pages/Inbound'));
const InboundDetail = React.lazy(() => import('@pages/InboundDetail'));
const Outbound = React.lazy(() => import('@pages/Outbound'));
const OutboundDetail = React.lazy(() => import('@pages/OutboundDetail'));
const Returns = React.lazy(() => import('@pages/Returns'));
const Tendering = React.lazy(() => import('@pages/Tendering'));
const LiveTracking = React.lazy(() => import('@pages/LiveTracking'));
const Settings = React.lazy(() => import('@pages/Settings'));
// Unfinished mock-only pages remain in src/pages/* but routes render ModuleUnderDevelopment.
const UploadTest = React.lazy(() => import('@pages/UploadTest'));
const InviteAccept = React.lazy(() => import('@pages/InviteAccept'));
const PlatformDashboard = React.lazy(() => import('@pages/platform/PlatformDashboard'));
const PlatformTenants = React.lazy(() => import('@pages/platform/PlatformTenants'));
const PlatformTenantDetail = React.lazy(() => import('@pages/platform/PlatformTenantDetail'));
const PlatformCreateTenant = React.lazy(() => import('@pages/platform/PlatformCreateTenant'));
const PlatformAnalytics = React.lazy(() => import('@pages/platform/PlatformAnalytics'));
const PlatformAudit = React.lazy(() => import('@pages/platform/PlatformAudit'));
const PlatformSettings = React.lazy(() => import('@pages/platform/PlatformSettings'));
const OnboardingWizard = React.lazy(() => import('@pages/onboarding/OnboardingWizard'));
const Customers = React.lazy(() => import('@pages/Customers'));
const RequirePlatformAdmin = React.lazy(() => import('./guards/RequirePlatformAdmin'));
const PortalLogin = React.lazy(() => import('@pages/portal/PortalLogin'));
const PortalForgotPassword = React.lazy(() => import('@pages/portal/PortalForgotPassword'));
const PortalResetPassword = React.lazy(() => import('@pages/portal/PortalResetPassword'));
const PortalAcceptInvite = React.lazy(() => import('@pages/portal/PortalAcceptInvite'));
const PortalHome = React.lazy(() => import('@pages/portal/PortalHome'));
const PortalProfile = React.lazy(() => import('@pages/portal/PortalProfile'));
const PortalRequisitions = React.lazy(() => import('@pages/portal/PortalRequisitions'));
const PortalRequisitionNew = React.lazy(() => import('@pages/portal/PortalRequisitionNew'));
const PortalRequisitionDetail = React.lazy(() => import('@pages/portal/PortalRequisitionDetail'));
const CustomerCreate = React.lazy(() => import('@pages/settings/CustomerCreate'));
const PaymentModes = React.lazy(() => import('@pages/settings/PaymentModes'));
const SettingsCustomers = React.lazy(() => import('@pages/settings/SettingsCustomers'));
const CustomerDetail = React.lazy(() => import('@pages/settings/CustomerDetail'));
const CustomerEdit = React.lazy(() => import('@pages/settings/CustomerEdit'));
const PlanningRequisitions = React.lazy(() => import('@pages/planning/PlanningRequisitions'));
const PlanningRequisitionDetail = React.lazy(() => import('@pages/planning/PlanningRequisitionDetail'));
const PlanningApprovals = React.lazy(() => import('@pages/planning/PlanningApprovals'));
const PlanningAuctions = React.lazy(() => import('@pages/planning/PlanningAuctions'));
const SettingsVendors = React.lazy(() => import('@pages/settings/SettingsVendors'));
const VendorCreate = React.lazy(() => import('@pages/settings/VendorCreate'));
const SettingsRateMaster = React.lazy(() => import('@pages/settings/SettingsRateMaster'));
const SettingsRateMasterDetail = React.lazy(() => import('@pages/settings/SettingsRateMasterDetail'));
const TenantAudit = React.lazy(() => import('@pages/settings/TenantAudit'));
const NotificationsPage = React.lazy(() => import('@pages/Notifications'));
const VendorRfqPage = React.lazy(() => import('@pages/vendor/VendorRfqPage'));

const LoadingFallback = () => (
  <div className="flex h-screen items-center justify-center bg-background">
    <div className="text-center">
      <div className="mx-auto h-12 w-12 animate-spin rounded-full border-4 border-ring border-t-transparent shadow-glow-purple" />
      <p className="mt-4 text-muted-foreground">Loading...</p>
    </div>
  </div>
);

const RequireAuth: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) return <LoadingFallback />;
  if (!isAuthenticated) return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  return <>{children}</>;
};

/** Production tenant gate — demo mode never bypasses this. */
const RequireCompany: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, activeCompanyId, memberships, hasActiveTenant, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) return <LoadingFallback />;
  if (user && !hasActiveTenant && memberships.length === 0) {
    if (location.pathname === '/company/setup') {
      return <>{children}</>;
    }
    return <Navigate to="/company/setup" replace state={{ from: location.pathname }} />;
  }
  if (user && !hasActiveTenant && memberships.length > 0 && !activeCompanyId) {
    return <Navigate to="/settings" replace state={{ from: location.pathname }} />;
  }
  return <>{children}</>;
};

const ProtectedRoute: React.FC<{ children: React.ReactNode; skipOnboarding?: boolean }> = ({
  children,
  skipOnboarding = false,
}) => (
  <RequireAuth>
    <RequireCompany>
      {skipOnboarding ? children : <RequireOnboardingComplete>{children}</RequireOnboardingComplete>}
    </RequireCompany>
  </RequireAuth>
);

const ProtectedPortalRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = usePortalAuth();
  const location = useLocation();

  if (isLoading) return <LoadingFallback />;
  if (!isAuthenticated) {
    return <Navigate to="/portal/login" replace state={{ from: location.pathname }} />;
  }
  return <>{children}</>;
};

const DemoSandboxRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <RequireAuth>
    <RequireDemoSandbox>{children}</RequireDemoSandbox>
  </RequireAuth>
);

/** Unfinished mock-only modules: keep page source, do not expose fake ops UI. */
const DemoModuleUnavailable: React.FC<{ title: string }> = ({ title }) => (
  <DemoSandboxRoute>
    <DashboardLayout>
      <ModuleUnderDevelopment title={title} backTo="/demo" />
    </DashboardLayout>
  </DemoSandboxRoute>
);

const LegacyShipmentRedirect: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'loading' | 'unlinked' | 'error'>(
    id ? 'loading' : 'error',
  );

  useEffect(() => {
    if (!id) return;
    let cancelled = false;

    (async () => {
      try {
        const shipment = await shipmentsService.getById(id);
        if (cancelled) return;

        if (shipment.docketId) {
          navigate(`/dockets/${shipment.docketId}`, { replace: true });
          return;
        }

        setStatus('unlinked');
      } catch {
        if (!cancelled) setStatus('error');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [id, navigate]);

  if (status === 'loading') {
    return <LoadingFallback />;
  }

  if (status === 'unlinked') {
    return <Navigate to="/tracking" replace state={{ error: 'No docket linked' }} />;
  }

  return <Navigate to="/dockets" replace />;
};

const LegacyDocketDemoRedirect: React.FC = () => {
  const { scenario } = useParams<{ scenario: string }>();
  return <Navigate to={scenario ? `/demo/dockets/scenario/${scenario}` : '/demo/dockets'} replace />;
};

const LegacyVendorQuoteRedirect: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  return <Navigate to={token ? `/q/${token}` : '/'} replace />;
};

function App() {
  useEffect(() => {
    void notificationsService.hydrateFeatureFlags();
  }, []);

  return (
    <AuthProvider>
      <ToastProvider>
        <PortalAuthProvider>
          {/*
            Page title context must sit above every page component that may call
            usePageTitle() during render — before DashboardLayout/PlatformLayout JSX.
          */}
          <PageTitleProvider>
            <div className="min-h-screen">
              <React.Suspense fallback={<LoadingFallback />}>
                <Routes>
                <Route path="/" element={<Landing />} />
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/verify-email" element={<VerifyEmail />} />
                <Route path="/invite/accept" element={<InviteAccept />} />

                {/* Platform Administration — outside tenant context */}
                <Route path="/platform" element={<RequirePlatformAdmin><PlatformDashboard /></RequirePlatformAdmin>} />
                <Route path="/platform/tenants" element={<RequirePlatformAdmin><PlatformTenants /></RequirePlatformAdmin>} />
                <Route path="/platform/tenants/new" element={<RequirePlatformAdmin><PlatformCreateTenant /></RequirePlatformAdmin>} />
                <Route path="/platform/tenants/:id" element={<RequirePlatformAdmin><PlatformTenantDetail /></RequirePlatformAdmin>} />
                <Route path="/platform/analytics" element={<RequirePlatformAdmin><PlatformAnalytics /></RequirePlatformAdmin>} />
                <Route path="/platform/audit" element={<RequirePlatformAdmin><PlatformAudit /></RequirePlatformAdmin>} />
                <Route path="/platform/settings" element={<RequirePlatformAdmin><PlatformSettings /></RequirePlatformAdmin>} />

                <Route path="/onboarding" element={<ProtectedRoute skipOnboarding><OnboardingWizard /></ProtectedRoute>} />

                {/* Engineer A — Customer portal */}
                <Route path="/portal/login" element={<PortalLogin />} />
                <Route path="/portal/forgot-password" element={<PortalForgotPassword />} />
                <Route path="/portal/reset-password" element={<PortalResetPassword />} />
                <Route path="/portal/accept-invite" element={<PortalAcceptInvite />} />
                <Route path="/portal" element={<ProtectedPortalRoute><PortalHome /></ProtectedPortalRoute>} />
                <Route path="/portal/profile" element={<ProtectedPortalRoute><PortalProfile /></ProtectedPortalRoute>} />
                <Route path="/portal/requisitions" element={<ProtectedPortalRoute><PortalRequisitions /></ProtectedPortalRoute>} />
                <Route path="/portal/requisitions/bulk" element={<Navigate to="/portal/requisitions" replace />} />
                <Route path="/portal/requisitions/new" element={<ProtectedPortalRoute><PortalRequisitionNew /></ProtectedPortalRoute>} />
                <Route
                  path="/portal/requisitions/draft/:draftId"
                  element={
                    <ProtectedPortalRoute>
                      <PortalRequisitionNew />
                    </ProtectedPortalRoute>
                  }
                />
                <Route path="/portal/requisitions/:id" element={<ProtectedPortalRoute><PortalRequisitionDetail /></ProtectedPortalRoute>} />

                {/* Public vendor quote page — no auth */}
                <Route path="/q/:token" element={<VendorRfqPage />} />
                <Route path="/rfq/:token" element={<LegacyVendorQuoteRedirect />} />

                {/* Production — always requires active tenant */}
                <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
                <Route
                  path="/company/setup"
                  element={
                    <RequireAuth>
                      <AuthenticatedLayout>
                        <CompanySetup />
                      </AuthenticatedLayout>
                    </RequireAuth>
                  }
                />
                <Route path="/dockets" element={<ProtectedRoute><RequireMembership roles={MEMBERSHIP_DOCKET_READ}><Dockets /></RequireMembership></ProtectedRoute>} />
                <Route path="/dispatch" element={<ProtectedRoute><RequireMembership roles={MEMBERSHIP_OPS_WRITE}><DispatchBoard /></RequireMembership></ProtectedRoute>} />
                <Route path="/dockets/:id/print/lr" element={<ProtectedRoute><RequireMembership roles={MEMBERSHIP_DOCKET_READ}><DocketLrPrintPage /></RequireMembership></ProtectedRoute>} />
                <Route path="/dockets/:id" element={<ProtectedRoute><RequireMembership roles={MEMBERSHIP_DOCKET_READ}><DocketDetail /></RequireMembership></ProtectedRoute>} />
                <Route path="/tracking" element={<ProtectedRoute><Tracking /></ProtectedRoute>} />
                <Route path="/my-trips" element={<ProtectedRoute><RequireMembership roles={MEMBERSHIP_TRIP_EXECUTE}><MyTrips /></RequireMembership></ProtectedRoute>} />
                <Route path="/carriers" element={<ProtectedRoute><RequireMembership roles={MEMBERSHIP_OPS_READ}><Carriers /></RequireMembership></ProtectedRoute>} />
                <Route path="/carriers/:id" element={<ProtectedRoute><RequireMembership roles={MEMBERSHIP_OPS_READ}><CarrierDetail /></RequireMembership></ProtectedRoute>} />
                <Route path="/billing" element={<ProtectedRoute><RequireMembership roles={MEMBERSHIP_BILLING_READ}><Invoices /></RequireMembership></ProtectedRoute>} />
                <Route path="/billing/:id" element={<ProtectedRoute><RequireMembership roles={MEMBERSHIP_BILLING_READ}><InvoiceDetail /></RequireMembership></ProtectedRoute>} />
                <Route path="/analytics" element={<ProtectedRoute><RequireMembership roles={MEMBERSHIP_COMMERCIAL_READ}><Analytics /></RequireMembership></ProtectedRoute>} />
                <Route path="/reports" element={<ProtectedRoute><RequireMembership roles={MEMBERSHIP_COMMERCIAL_READ}><Reports /></RequireMembership></ProtectedRoute>} />
                <Route path="/reports/commercial" element={<ProtectedRoute><RequireMembership roles={MEMBERSHIP_COMMERCIAL_READ}><CommercialAnalytics /></RequireMembership></ProtectedRoute>} />
                <Route path="/live-tracking" element={<ProtectedRoute><RequireGpsLive><LiveTracking /></RequireGpsLive></ProtectedRoute>} />
                <Route path="/fleet" element={<ProtectedRoute><RequireMembership roles={MEMBERSHIP_FLEET_READ}><Fleet /></RequireMembership></ProtectedRoute>} />
                <Route path="/fleet/drivers/:driverId" element={<ProtectedRoute><RequireMembership roles={MEMBERSHIP_FLEET_READ}><DriverDetail /></RequireMembership></ProtectedRoute>} />
                <Route path="/fleet/:id" element={<ProtectedRoute><RequireMembership roles={MEMBERSHIP_FLEET_READ}><VehicleDetail /></RequireMembership></ProtectedRoute>} />
                <Route path="/customers" element={<ProtectedRoute><Customers /></ProtectedRoute>} />
                <Route path="/routes" element={<ProtectedRoute><RequireRoutesPlanner><RoutesPage /></RequireRoutesPlanner></ProtectedRoute>} />
                <Route path="/route-planning" element={<ProtectedRoute><RequireMembership roles={MEMBERSHIP_OPS_READ}><RoutePlanning /></RequireMembership></ProtectedRoute>} />
                <Route path="/notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />
                <Route path="/settings" element={<ProtectedRoute><RequireMembership roles={MEMBERSHIP_ADMIN_WRITE}><Settings /></RequireMembership></ProtectedRoute>} />
                <Route path="/upload-test" element={<ProtectedRoute><UploadTest /></ProtectedRoute>} />
                <Route path="/settings/customers" element={<ProtectedRoute><RequireMembership roles={MEMBERSHIP_ADMIN_WRITE}><SettingsCustomers /></RequireMembership></ProtectedRoute>} />
                <Route path="/settings/customers/new" element={<ProtectedRoute><RequireMembership roles={MEMBERSHIP_ADMIN_WRITE}><CustomerCreate /></RequireMembership></ProtectedRoute>} />
                <Route path="/settings/customers/:id" element={<ProtectedRoute><RequireMembership roles={MEMBERSHIP_ADMIN_WRITE}><CustomerDetail /></RequireMembership></ProtectedRoute>} />
                <Route path="/settings/customers/:id/edit" element={<ProtectedRoute><RequireMembership roles={MEMBERSHIP_ADMIN_WRITE}><CustomerEdit /></RequireMembership></ProtectedRoute>} />
                <Route path="/settings/payment-modes" element={<ProtectedRoute><RequireMembership roles={MEMBERSHIP_ADMIN_WRITE}><PaymentModes /></RequireMembership></ProtectedRoute>} />
                <Route path="/settings/vendors" element={<ProtectedRoute><RequireMembership roles={MEMBERSHIP_ADMIN_WRITE}><SettingsVendors /></RequireMembership></ProtectedRoute>} />
                <Route path="/settings/vendors/new" element={<ProtectedRoute><RequireMembership roles={MEMBERSHIP_ADMIN_WRITE}><VendorCreate /></RequireMembership></ProtectedRoute>} />
                <Route path="/settings/rate-master" element={<ProtectedRoute><RequireMembership roles={MEMBERSHIP_ADMIN_WRITE}><SettingsRateMaster /></RequireMembership></ProtectedRoute>} />
                <Route path="/settings/rate-master/:id" element={<ProtectedRoute><RequireMembership roles={MEMBERSHIP_ADMIN_WRITE}><SettingsRateMasterDetail /></RequireMembership></ProtectedRoute>} />
                <Route path="/settings/audit" element={<ProtectedRoute><RequireMembership roles={MEMBERSHIP_AUDIT_READ}><TenantAudit /></RequireMembership></ProtectedRoute>} />
                <Route path="/planning/requisitions" element={<ProtectedRoute><RequireMembership roles={MEMBERSHIP_OPS_READ}><PlanningRequisitions /></RequireMembership></ProtectedRoute>} />
                <Route path="/planning/requisitions/bulk" element={<Navigate to="/planning/requisitions" replace />} />
                <Route path="/planning/requisitions/:id" element={<ProtectedRoute><RequireMembership roles={MEMBERSHIP_OPS_READ}><PlanningRequisitionDetail /></RequireMembership></ProtectedRoute>} />
                <Route path="/planning/approvals" element={<ProtectedRoute><RequireMembership roles={MEMBERSHIP_APPROVAL_READ}><PlanningApprovals /></RequireMembership></ProtectedRoute>} />
                <Route path="/planning/auctions" element={<ProtectedRoute><RequireMembership roles={MEMBERSHIP_OPS_READ}><PlanningAuctions /></RequireMembership></ProtectedRoute>} />

                {/* Demo sandbox hub (auth required; tenant not required; blocked in production) */}
                <Route path="/demo" element={<RequireAuth><RequireDemoAllowed><DemoWorkspace /></RequireDemoAllowed></RequireAuth>} />

                {/* Demo sandbox modules — simulated data only */}
                <Route path="/demo/dashboard" element={<RequireDemoAllowed><DemoSandboxRoute><Dashboard /></DemoSandboxRoute></RequireDemoAllowed>} />
                <Route path="/demo/dockets" element={<RequireDemoAllowed><DemoSandboxRoute><Dockets /></DemoSandboxRoute></RequireDemoAllowed>} />
                <Route path="/demo/dockets/scenario/:scenario" element={<RequireDemoAllowed><DemoSandboxRoute><DocketDetail /></DemoSandboxRoute></RequireDemoAllowed>} />
                <Route path="/demo/tracking" element={<RequireDemoAllowed><DemoSandboxRoute><Tracking /></DemoSandboxRoute></RequireDemoAllowed>} />
                <Route path="/demo/fleet" element={<RequireDemoAllowed><DemoSandboxRoute><Fleet /></DemoSandboxRoute></RequireDemoAllowed>} />
                <Route path="/demo/fleet/:id" element={<RequireDemoAllowed><DemoSandboxRoute><VehicleDetail /></DemoSandboxRoute></RequireDemoAllowed>} />
                <Route path="/demo/routes" element={<RequireDemoAllowed><DemoSandboxRoute><RoutesPage /></DemoSandboxRoute></RequireDemoAllowed>} />
                <Route path="/demo/route-planning" element={<RequireDemoAllowed><DemoSandboxRoute><RoutePlanning /></DemoSandboxRoute></RequireDemoAllowed>} />
                <Route path="/demo/live-tracking" element={<RequireDemoAllowed><DemoSandboxRoute><LiveTracking /></DemoSandboxRoute></RequireDemoAllowed>} />
                <Route path="/demo/analytics" element={<RequireDemoAllowed><DemoSandboxRoute><Analytics /></DemoSandboxRoute></RequireDemoAllowed>} />
                <Route path="/demo/inbound" element={<RequireDemoAllowed><DemoModuleUnavailable title="Inbound" /></RequireDemoAllowed>} />
                <Route path="/demo/inbound/:id" element={<RequireDemoAllowed><DemoModuleUnavailable title="Inbound detail" /></RequireDemoAllowed>} />
                <Route path="/demo/outbound" element={<RequireDemoAllowed><DemoModuleUnavailable title="Outbound" /></RequireDemoAllowed>} />
                <Route path="/demo/outbound/:id" element={<RequireDemoAllowed><DemoModuleUnavailable title="Outbound detail" /></RequireDemoAllowed>} />
                <Route path="/demo/returns" element={<RequireDemoAllowed><DemoModuleUnavailable title="Returns" /></RequireDemoAllowed>} />
                <Route path="/demo/yard" element={<RequireDemoAllowed><DemoModuleUnavailable title="Yard management" /></RequireDemoAllowed>} />
                <Route path="/demo/gate-pass" element={<RequireDemoAllowed><DemoModuleUnavailable title="Gate pass" /></RequireDemoAllowed>} />
                <Route path="/demo/dock-scheduling" element={<RequireDemoAllowed><DemoModuleUnavailable title="Dock scheduling" /></RequireDemoAllowed>} />
                <Route path="/demo/tendering" element={<RequireDemoAllowed><DemoModuleUnavailable title="Tendering" /></RequireDemoAllowed>} />
                <Route path="/demo/vehicle-capacity" element={<RequireDemoAllowed><DemoModuleUnavailable title="Vehicle capacity" /></RequireDemoAllowed>} />

                {/* Legacy redirects */}
                <Route path="/shipments" element={<Navigate to="/dockets" replace />} />
                <Route path="/shipments/:id" element={<LegacyShipmentRedirect />} />
                <Route path="/tracking/:id" element={<LegacyShipmentRedirect />} />
                <Route path="/loading" element={<Navigate to="/dockets" replace />} />
                <Route path="/dockets/demo/:scenario" element={<LegacyDocketDemoRedirect />} />

                {/* Demo-only legacy production paths → sandbox (blocked when demo disabled) */}
                <Route path="/inbound" element={<RequireDemoAllowed><Navigate to="/demo/inbound" replace /></RequireDemoAllowed>} />
                <Route path="/inbound/:id" element={<RequireDemoAllowed><Navigate to="/demo/inbound" replace /></RequireDemoAllowed>} />
                <Route path="/outbound" element={<RequireDemoAllowed><Navigate to="/demo/outbound" replace /></RequireDemoAllowed>} />
                <Route path="/outbound/:id" element={<RequireDemoAllowed><Navigate to="/demo/outbound" replace /></RequireDemoAllowed>} />
                <Route path="/returns" element={<RequireDemoAllowed><Navigate to="/demo/returns" replace /></RequireDemoAllowed>} />
                <Route path="/yard" element={<RequireDemoAllowed><Navigate to="/demo/yard" replace /></RequireDemoAllowed>} />
                <Route path="/gate-pass" element={<RequireDemoAllowed><Navigate to="/demo/gate-pass" replace /></RequireDemoAllowed>} />
                <Route path="/dock-scheduling" element={<RequireDemoAllowed><Navigate to="/demo/dock-scheduling" replace /></RequireDemoAllowed>} />
                <Route path="/tendering" element={<RequireDemoAllowed><Navigate to="/demo/tendering" replace /></RequireDemoAllowed>} />
                <Route path="/vehicle-capacity" element={<RequireDemoAllowed><Navigate to="/demo/vehicle-capacity" replace /></RequireDemoAllowed>} />

                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </React.Suspense>
          </div>
          </PageTitleProvider>
        </PortalAuthProvider>
      </ToastProvider>
    </AuthProvider>
  );
}

export default App;
